// Base API configuration
import axios from 'axios';
import Cookies from 'js-cookie';
import { authDB } from '../db/indexedDB';

export const BASE_URL = 'https://api.decomaster.co.zw';

// Token expiration times
export const TOKEN_EXPIRY_HOURS = 6; // Access token expires in 6 hours
const REFRESH_TOKEN_EXPIRY_DAYS = 10; // Refresh token expires in 10 days (backend setting)
const REFRESH_INTERVAL_HOURS = 5; // Refresh token every 5 hours (before expiration)
const REFRESH_INTERVAL_MS = REFRESH_INTERVAL_HOURS * 60 * 60 * 1000; // 5 hours in milliseconds
export const ACCESS_TOKEN_TTL_MS = TOKEN_EXPIRY_HOURS * 60 * 60 * 1000;

// Base cookie options
const baseCookieOptions = {
  secure: window.location.protocol === 'https:', // Only send over HTTPS in production
  sameSite: 'strict', // CSRF protection
};

// Cookie configuration for access token - 6 hours expiration
const ACCESS_TOKEN_COOKIE_OPTIONS = {
  ...baseCookieOptions,
  expires: TOKEN_EXPIRY_HOURS / 24, // 6 hours = 0.25 days
};

// Cookie configuration for refresh token - 10 days expiration
const REFRESH_TOKEN_COOKIE_OPTIONS = {
  ...baseCookieOptions,
  expires: REFRESH_TOKEN_EXPIRY_DAYS, // 10 days
};

// Cookie configuration for user info - 10 days expiration (matches refresh token)
const USER_INFO_COOKIE_OPTIONS = {
  ...baseCookieOptions,
  expires: REFRESH_TOKEN_EXPIRY_DAYS, // 10 days
};

// API endpoints
export const API_ENDPOINTS = {
  LOGIN: `${BASE_URL}/api/accounts/login/`,
  REFRESH: `${BASE_URL}/api/accounts/token/refresh/`,
  DASHBOARD_ANALYTICS: `${BASE_URL}/api/analytics/dashboard/`,
  CHANGE_PASSWORD: `${BASE_URL}/api/accounts/change-password/`,
  RESET_PASSWORD: `${BASE_URL}/api/accounts/reset-password/`,
  CREATE_USER: `${BASE_URL}/api/accounts/create-user/`,
  BRANCHES: `${BASE_URL}/api/organization/branches/`,
  WAREHOUSES: `${BASE_URL}/api/organization/warehouses/`,
  CATEGORIES: `${BASE_URL}/api/products/categories/`,
  PRODUCTS: `${BASE_URL}/api/products/products/`,
  SUPPLIERS: `${BASE_URL}/api/stock/suppliers/`,
  STOCK_TRANSFERS: `${BASE_URL}/api/stock/transfers/`,
  STOCK_TRANSFERS_BULK: `${BASE_URL}/api/stock/transfers/bulk/`,
  ADMIN_MOVE_TO_BRANCH: `${BASE_URL}/api/stock/transfers/admin/move-to-branch/`,
  ADMIN_MOVE_TO_BRANCH_EXCEL: `${BASE_URL}/api/stock/transfers/admin/move-to-branch/import-excel/`,
  ADMIN_MOVE_BRANCH_TO_BRANCH: `${BASE_URL}/api/stock/transfers/admin/move-branch-to-branch/`,
  ADMIN_MOVE_BRANCH_TO_BRANCH_EXCEL: `${BASE_URL}/api/stock/transfers/admin/move-branch-to-branch/import-excel/`,
  BRANCH_STOCK: `${BASE_URL}/api/stock/branch-stock/`,
  BRANCH_STOCK_DETAIL: (id) => `${BASE_URL}/api/stock/branch-stock/${id}/`,
  BRANCH_STOCK_SUMMARY: `${BASE_URL}/api/stock/branch-stock/summary/`,
  BRANCH_STOCK_VALUE: `${BASE_URL}/api/stock/branch-stock/value/`,
  LOW_STOCK: `${BASE_URL}/api/stock/low-stock/`,
  BRANCH_LOW_STOCK: `${BASE_URL}/api/stock/branch-low-stock/`,
  SALES: `${BASE_URL}/api/sales/`,
  SALES_HISTORY: `${BASE_URL}/api/sales/history/`,
  SALES_REPORT: `${BASE_URL}/api/accounting/sales-report/`,
  EXPENSE_CATEGORIES: `${BASE_URL}/api/accounting/expense-categories/`,
  EXPENSES: `${BASE_URL}/api/accounting/expenses/`,
  PROFIT_LOSS: `${BASE_URL}/api/accounting/profit-loss/`,
  PROFILE: `${BASE_URL}/api/accounts/profile/`,
  AUDIT_LOGS: `${BASE_URL}/api/shared/audit-logs/`,
  ANALYTICS_REPORTS: `${BASE_URL}/api/analytics/reports/`,
  PRODUCT_BARCODES: `${BASE_URL}/api/products/barcodes/`,
  BRANCH_DASHBOARD: `${BASE_URL}/api/analytics/branch-dashboard/`,
  WAREHOUSE_DASHBOARD: `${BASE_URL}/api/analytics/warehouse-dashboard/`,
  BRANCH_REPORTS: `${BASE_URL}/api/analytics/branch-reports/`,
  WAREHOUSE_REPORTS: `${BASE_URL}/api/analytics/warehouse-reports/`,
  CASHIER_PERFORMANCE: `${BASE_URL}/api/analytics/cashier-performance/`,
  SALES_TRENDS: `${BASE_URL}/api/analytics/sales-trends/`,
  STOCK_MOVEMENT: `${BASE_URL}/api/analytics/stock-movement/`,
  SALES_REPORT_ANALYTICS: `${BASE_URL}/api/analytics/sales-report/`,
  STOCK_REPORT_ANALYTICS: `${BASE_URL}/api/analytics/stock-report/`,
  AUDITOR_REPORTS: `${BASE_URL}/api/analytics/auditor-reports/`,
  STOCK_ADJUSTMENTS: `${BASE_URL}/api/stock/adjustments/`,
  SALES_CASH_RECEIVED: `${BASE_URL}/api/sales/cash-received/`,
  SALES_CASH_VARIANCE: `${BASE_URL}/api/sales/cash-variance/`,
  SALES_RETURNS_CODE: `${BASE_URL}/api/sales/returns-code/`,
  SALES_RETURNS_RATE: `${BASE_URL}/api/sales/returns-rate/`,
  LAYBY: `${BASE_URL}/api/layby/`,
  LAYBY_PAYMENT: (id) => `${BASE_URL}/api/layby/${id}/payment/`,
};

// Token refresh interval ID
let refreshIntervalId = null;

// Helper function to get stored access token (checks both cookies and IndexedDB)
export const getAccessToken = async () => {
  // First check cookies (for backward compatibility)
  const cookieToken = Cookies.get('access_token');
  if (cookieToken) return cookieToken;

  // Then check IndexedDB
  try {
    const authData = await authDB.get();
    return authData?.access_token || null;
  } catch (error) {
    console.error('Error getting access token from IndexedDB:', error);
    return null;
  }
};

// Helper function to get stored refresh token (checks both cookies and IndexedDB)
export const getRefreshToken = async () => {
  // First check cookies (for backward compatibility)
  const cookieToken = Cookies.get('refresh_token');
  if (cookieToken) return cookieToken;

  // Then check IndexedDB
  try {
    const authData = await authDB.get();
    return authData?.refresh_token || null;
  } catch (error) {
    console.error('Error getting refresh token from IndexedDB:', error);
    return null;
  }
};

// Helper function to get stored user info (checks both cookies and IndexedDB)
export const getUserInfo = async () => {
  // First check cookies (for backward compatibility)
  const cookieUser = Cookies.get('user_info');
  if (cookieUser) {
    try {
      return JSON.parse(cookieUser);
    } catch (e) {
      // Invalid JSON, continue to IndexedDB
    }
  }

  // Then check IndexedDB
  try {
    const authData = await authDB.get();
    return authData?.user_info || null;
  } catch (error) {
    console.error('Error getting user info from IndexedDB:', error);
    return null;
  }
};

// Synchronous version for backward compatibility
export const getUserInfoSync = () => {
  const userStr = Cookies.get('user_info');
  return userStr ? JSON.parse(userStr) : null;
};

// Function to refresh access token
export const refreshAccessToken = async () => {
  try {
    const refreshToken = await getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    // Check if online
    if (!navigator.onLine) {
      // Return cached token if available
      const authData = await authDB.get();
      if (authData?.access_token) {
        return authData.access_token;
      }
      throw new Error('No network connection and no cached token');
    }

    const response = await axios.post(API_ENDPOINTS.REFRESH, {
      refresh: refreshToken,
    });

    const { access } = response.data;
    Cookies.set('access_token', access, ACCESS_TOKEN_COOKIE_OPTIONS);

    // Also save to IndexedDB
    const authData = await authDB.get();
    if (authData) {
      await authDB.save({
        ...authData,
        access_token: access,
        last_synced: new Date().toISOString(),
      });
    }

    return access;
  } catch (error) {
    console.error('Token refresh failed:', error);

    // If offline, try to use cached token
    if (!navigator.onLine) {
      const authData = await authDB.get();
      if (authData?.access_token) {
        return authData.access_token;
      }
    }

    // If refresh fails, clear auth and redirect to login
    clearAuthData();
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
    throw error;
  }
};

// Function to start automatic token refresh
const startTokenRefreshInterval = () => {
  // Clear any existing interval
  if (refreshIntervalId) {
    clearInterval(refreshIntervalId);
  }

  // Refresh token every 5 hours
  refreshIntervalId = setInterval(async () => {
    if (isAuthenticated()) {
      try {
        await refreshAccessToken();
        console.log('Token refreshed successfully');
      } catch (error) {
        console.error('Automatic token refresh failed:', error);
      }
    }
  }, REFRESH_INTERVAL_MS);
};

// Function to stop automatic token refresh
const stopTokenRefreshInterval = () => {
  if (refreshIntervalId) {
    clearInterval(refreshIntervalId);
    refreshIntervalId = null;
  }
};

// Helper function to store auth data (stores in both cookies and IndexedDB)
export const storeAuthData = async (accessToken, refreshToken, userInfo, options = {}) => {
  // Store in cookies FIRST (synchronously) so they're immediately available
  Cookies.set('access_token', accessToken, ACCESS_TOKEN_COOKIE_OPTIONS);
  Cookies.set('refresh_token', refreshToken, REFRESH_TOKEN_COOKIE_OPTIONS);
  Cookies.set('user_info', JSON.stringify(userInfo), USER_INFO_COOKIE_OPTIONS);

  // Verify cookies were set
  const verifyToken = Cookies.get('access_token');
  const verifyUser = Cookies.get('user_info');

  if (!verifyToken || !verifyUser) {
    console.error('Failed to set cookies. Token:', !!verifyToken, 'User:', !!verifyUser);
  }

  const accessExpiresAt = options.accessExpiresAt || new Date(Date.now() + TOKEN_EXPIRY_HOURS * 60 * 60 * 1000).toISOString();

  // Store in IndexedDB for offline access - await to ensure it's saved before navigation
  try {
    await authDB.save({
      access_token: accessToken,
      refresh_token: refreshToken,
      user_info: userInfo,
      expires_at: accessExpiresAt,
    });
  } catch (error) {
    console.error('Error saving auth data to IndexedDB:', error);
    // Don't throw - cookies are already set, so auth will still work
  }

  // Start automatic token refresh
  startTokenRefreshInterval();
};

// Helper function to clear auth data
export const clearAuthData = async () => {
  Cookies.remove('access_token');
  Cookies.remove('refresh_token');
  Cookies.remove('user_info');

  // Clear from IndexedDB
  try {
    await authDB.clear();
  } catch (error) {
    console.error('Error clearing auth data from IndexedDB:', error);
  }

  // Stop automatic token refresh
  stopTokenRefreshInterval();
};

// Helper function to check if user is authenticated
export const isAuthenticated = async () => {
  const token = await getAccessToken();
  return !!token;
};

// Synchronous version for backward compatibility
export const isAuthenticatedSync = () => {
  return !!Cookies.get('access_token');
};

// Configure axios instance with interceptors
// Create axios instance
const apiClient = axios.create({
  baseURL: BASE_URL,
});

// Request interceptor to add access token and handle offline
apiClient.interceptors.request.use(
  async (config) => {
    const token = await getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh on 401 (fallback if automatic refresh missed)
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 and we haven't already tried to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const access = await refreshAccessToken();

        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${access}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh failed, clear auth and redirect to login
        // (refreshAccessToken already handles this, but keeping for safety)
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Initialize token refresh interval if user is already authenticated (e.g., on page reload)
isAuthenticated().then((authenticated) => {
  if (authenticated) {
    startTokenRefreshInterval();
  }
});

const EXPIRY_BUFFER_MS = 60 * 1000; // 1 minute safety buffer

const tokenNeedsRefresh = (expiresAt) => {
  if (!expiresAt) return true;
  const expiry = new Date(expiresAt).getTime();
  return expiry - Date.now() <= EXPIRY_BUFFER_MS;
};

const notifyTokenRefreshFailure = () => {
  if (typeof window === 'undefined') return;
  if (window.__wealthbag_tokenRefreshAlerted) return;

  window.__wealthbag_tokenRefreshAlerted = true;
  const message = 'Your session expired while you were offline. Please log in online to sync pending work.';

  if (window.Swal?.fire) {
    window.Swal.fire({
      icon: 'warning',
      title: 'Session Expired',
      text: message,
      confirmButtonColor: '#3085d6',
    });
  } else if (typeof window.alert === 'function') {
    window.alert(message);
  } else {
    console.warn(message);
  }
};

const attemptSilentTokenRefresh = async () => {
  if (typeof window !== 'undefined' && window.__wealthbag_tokenRefreshing) {
    return;
  }

  if (typeof window !== 'undefined') {
    window.__wealthbag_tokenRefreshing = true;
  }

  try {
    await refreshAccessToken();
    if (typeof window !== 'undefined') {
      window.__wealthbag_tokenRefreshFailed = false;
      window.__wealthbag_tokenRefreshAlerted = false;
    }
  } catch (error) {
    if (typeof window !== 'undefined') {
      window.__wealthbag_tokenRefreshFailed = true;
    }
    notifyTokenRefreshFailure();
  } finally {
    if (typeof window !== 'undefined') {
      window.__wealthbag_tokenRefreshing = false;
    }
  }
};

const handleOnlineTokenRecovery = async () => {
  try {
    const authData = await authDB.get();
    if (!authData?.refresh_token) {
      return;
    }

    if (!tokenNeedsRefresh(authData.expires_at)) {
      return;
    }

    await attemptSilentTokenRefresh();
  } catch (error) {
    console.error('Token recovery failed when coming back online:', error);
  }
};

const registerOnlineTokenRecovery = () => {
  if (typeof window === 'undefined') return;
  const RECOVERY_KEY = '__wealthbag_token_recovery_registered';
  if (window[RECOVERY_KEY]) return;

  window[RECOVERY_KEY] = true;

  window.addEventListener('online', () => {
    handleOnlineTokenRecovery();
  });

  // Run once at startup if we're already online
  if (typeof navigator !== 'undefined' && navigator.onLine) {
    handleOnlineTokenRecovery();
  }
};

registerOnlineTokenRecovery();

export default apiClient;

